import type * as THREE from 'three';
import type { FloraEditor } from '@/editor/FloraEditor';
import type { SiteObject } from '@/site/SiteObject';
import type { FloraResolver } from '@/localization/FloraResolver';
import type { SiteLocalization } from '@/localization/SiteLocalization';
import {
  MONTH_LABELS,
  buildWildlifeCalendar,
  type CalendarEntry,
  type CalendarRow,
} from '@/localization/Ecology';
import { clear, el, formatLength, type Child } from './dom';
import type { LensDefinition, LensReading } from './FloraLens';
import { icon, type IconName } from './Icons';
import type { SiteSummary } from './InspectorPanel';
import { formatAge } from './PlantMetrics';
import { PlantMap } from './PlantMap';
import {
  assignObjectTags,
  objectPointerTarget,
  plantPointerTarget,
  type PointerDetail,
  type PointerTarget,
} from './PointerLayout';
import { ScenePointers } from './ScenePointers';
import type { UiPreferences } from './UiPreferences';
import {
  AILING_HEALTH,
  buildPlantManifest,
  containerLabel,
  formatArea,
  matchesQuery,
  plantsCsv,
  scheduleCsv,
  sortSpecies,
  SCHEDULE_SORTS,
  type ManifestPlant,
  type ManifestSpecies,
  type PlantManifest,
  type ScheduleSort,
} from './PlantManifest';

/**
 * The plant manifest: the site read as a document instead of as a model.
 *
 * It answers the two questions a rendering cannot. *What did I actually plant* — the schedule, one
 * line per species with quantities and nursery sizes, which is the sheet that gets ordered from and
 * checked off on installation. And *which one is that* — every specimen, tagged and numbered, each
 * row wired back to the selection so reading the report and working the model are the same act.
 *
 * It lives where the Render button lives — pinned to the canvas column, opposite corner — because
 * both are things you do *to the view* rather than tools you pick up. The sheet is deliberately
 * non-modal: it floats over the canvas with the scene still live behind it, so clicking a species
 * row lights up its plants in the viewport around the card. A modal backdrop would hide the one
 * thing the click was for.
 *
 * The plan map hangs off this panel rather than off the toolbar, for the same reason: it is the same
 * manifest, plotted. See `PlantMap.ts`.
 */

export type ManifestView = 'schedule' | 'plants' | 'ecology';

/** Rows added per "show more" press. Big sites are common; a 40 000-row table is not usable. */
const PLANT_PAGE_SIZE = 250;

export interface PlantManifestPanelDeps {
  editor: FloraEditor;
  preferences: UiPreferences;
  /** Site dimensions, for the plan map's extent and the header summary. */
  site: () => SiteSummary | null;
  /** Buildings and paving, drawn on the plan for context. */
  siteObjects?: () => readonly SiteObject[];
  /** Frame the camera on these plants — used by row double-click and the map. */
  onFocus?: (instanceIds: readonly string[]) => void;
  /**
   * The site's flora resolver. Given one, the manifest gains an **Ecology** view: the same planting
   * read as a food and habitat calendar. Omitted, the tab does not exist — there is no honest
   * wildlife report to draw without resolved phenology to hang it on.
   */
  resolver?: () => FloraResolver;
  /** Where the site is, so the report can say which climate its months were derived from. */
  localization?: () => SiteLocalization | null;
  /**
   * The live camera and the canvas it draws into. Given both, the bar gains a **Pointers** toggle:
   * the manifest printed onto the garden itself, one callout per plant carrying the same tag number
   * the Plants tab lists it under.
   *
   * Omitted, the control does not exist — there is nothing honest to project without a camera, and a
   * dead button is worse than an absent one. Same rule as the Ecology tab and its resolver.
   */
  camera?: () => THREE.Camera;
  canvas?: () => HTMLElement | null;
  /**
   * How a plant reads against the lens the design view is on. Given it, {@link openMap} can dress the
   * plan in that lens; omitted, the map is the plain plan it has always been.
   *
   * The reading is supplied rather than computed here so the plan and the 3D view are literally the
   * same numbers — the lens bar owns which lens and which date, and both drawings ask it.
   */
  lensReading?: (plant: ManifestPlant, lens: LensDefinition) => LensReading;
}

export class PlantManifestPanel {
  /** Mounted over the canvas column; the bar pins itself to the top-left corner of it. */
  readonly root: HTMLElement;

  private view: ManifestView = 'schedule';
  private sort: ScheduleSort = 'section';
  private query = '';
  private plantLimit = PLANT_PAGE_SIZE;
  /** Rebuilt lazily — every edit invalidates it, but only an open sheet pays to recompute. */
  private cache: PlantManifest | null = null;
  private frameHandle: number | null = null;

  private readonly bar: HTMLElement;
  /** The corner row the bar stands in, shared with whatever {@link mountTrailing} puts beside it. */
  private readonly corner: HTMLElement;
  private readonly openButton: HTMLButtonElement;
  private readonly countChip: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly subtitle: HTMLElement;
  private readonly tabs: HTMLElement;
  private readonly searchInput: HTMLInputElement;
  private readonly sortSelect: HTMLSelectElement;
  private readonly body: HTMLElement;
  private readonly footer: HTMLElement;
  private readonly note: HTMLElement;
  private readonly map: PlantMap;
  private readonly unsubscribe: (() => void)[] = [];

  /** The callout overlay, and its controls. All null when the host supplied no camera. */
  private pointers: ScenePointers | null = null;
  private pointerButton: HTMLButtonElement | null = null;
  private pointerMenu: HTMLElement | null = null;
  /** Callout targets for the current document, rebuilt with the manifest rather than per frame. */
  private pointerCache: PointerTarget[] | null = null;

  /**
   * What is being pointed at or hovered right now, in either direction.
   *
   * One set read by both renderers, which is what makes the tag number mean something rather than
   * merely be printed twice: hovering a schedule line lights its plants in the viewport, and hovering
   * a callout lights its row in the sheet.
   */
  private hot: ReadonlySet<string> = new Set();
  /** Rows on screen, by the id they stand for, so a hover can be applied without a rebuild. */
  private readonly rowsById = new Map<string, HTMLElement[]>();

  /**
   * Escape, unwound one layer at a time: the plan map, then a filter that is hiding rows, then the
   * sheet itself. Each press undoes the last thing that narrowed what you can see.
   */
  private readonly onWindowKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (!this.isOpen && !this.map.isOpen) return;
    event.stopPropagation();
    event.preventDefault();
    if (this.map.isOpen) {
      this.map.close();
      return;
    }
    if (this.query !== '') {
      this.query = '';
      this.searchInput.value = '';
      this.plantLimit = PLANT_PAGE_SIZE;
      this.render();
      return;
    }
    this.close();
  };

  constructor(private readonly deps: PlantManifestPanelDeps) {
    this.countChip = el('span', { class: 'lg-manifest__count', text: '0' });
    this.openButton = el('button', {
      class: 'lg-manifest__open',
      attrs: { type: 'button', 'aria-expanded': 'false' },
      title: 'Plant manifest — every plant on this site, counted and scheduled',
      on: { click: () => this.toggle() },
    });
    this.openButton.append(
      el('span', { class: 'lg-manifest__openicon', html: icon('list', 16) }),
      el('span', { class: 'lg-manifest__label', text: 'Manifest' }),
      this.countChip,
    );
    this.bar = el('div', { class: 'lg-manifest__bar' }, this.openButton);
    this.corner = el('div', { class: 'lg-manifest__corner' }, this.bar);

    this.subtitle = el('span', { class: 'lg-manifest__subtitle' });
    this.tabs = el('div', { class: 'lg-segment lg-segment--tight', attrs: { role: 'group', 'aria-label': 'Manifest view' } });
    this.body = el('div', { class: 'lg-manifest__body' });
    this.note = el('span', { class: 'lg-manifest__note' });
    this.footer = el('footer', { class: 'lg-manifest__footer' }, this.note);

    this.searchInput = el('input', {
      class: 'lg-manifest__search',
      attrs: {
        type: 'search',
        placeholder: 'Filter by name, group, or size…',
        'aria-label': 'Filter the manifest',
        spellcheck: 'false',
      },
      on: {
        input: () => {
          this.query = this.searchInput.value;
          this.plantLimit = PLANT_PAGE_SIZE;
          this.render();
        },
      },
    });

    this.sortSelect = el('select', {
      class: 'lg-manifest__select',
      attrs: { 'aria-label': 'Sort the schedule' },
      on: {
        change: () => {
          this.sort = this.sortSelect.value as ScheduleSort;
          this.render();
        },
      },
    });
    for (const option of SCHEDULE_SORTS) {
      this.sortSelect.appendChild(el('option', { text: option.label, attrs: { value: option.value } }));
    }

    this.sheet = this.buildSheet();
    this.root = el('div', { class: 'lg-manifest' }, this.corner, this.sheet);

    this.map = new PlantMap({
      host: this.root,
      manifest: () => this.manifest(),
      site: () => deps.site(),
      structures: () => deps.siteObjects?.() ?? [],
      unit: () => deps.preferences.unit,
      isSelected: (id) => deps.editor.selection.isSelected(id),
      onSelect: (ids, additive) => this.select(ids, additive),
      onFocus: (ids) => this.focus(ids),
      onClose: () => this.openButton.focus(),
      ...(deps.lensReading ? { reading: deps.lensReading } : {}),
    });

    // The callouts hang off this panel for the same reason the plan map does: they are the same
    // manifest, drawn somewhere else. Sharing the panel's cache is not a convenience — it is what
    // guarantees a label and a row can never disagree about a plant's number.
    this.buildPointers();

    // Escape is claimed on `window`, in the capture phase, rather than on the sheet: rebuilding a
    // table or a legend replaces the element that had focus, and a listener hung off the panel
    // would then never see the key at all. Capture also puts it ahead of the editor's own window
    // listener, so Escape closes the report instead of clearing the selection behind it.
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onWindowKeyDown, true);
      this.unsubscribe.push(() => window.removeEventListener('keydown', this.onWindowKeyDown, true));
    }

    // Any edit changes the report: plants added, aged, moved, deleted, undone.
    this.unsubscribe.push(deps.editor.onChange(() => this.invalidate()));
    this.unsubscribe.push(deps.editor.selection.onChange(() => this.requestRender()));
    this.unsubscribe.push(deps.preferences.onChange(() => this.requestRender()));

    this.setView('schedule');
    this.refreshCount();
  }

  get isOpen(): boolean {
    return this.sheet.hidden === false;
  }

  /**
   * Open the plan map, optionally reading a lens — the entry point for the bar at the foot of the
   * design view.
   *
   * The map stays owned by this panel deliberately. Its numbers come from this panel's manifest
   * cache, and that shared cache is what guarantees a callout, a table row and a canopy on the plan
   * can never disagree about which plant is #17. A second map built beside it would have no such
   * guarantee, so the bar borrows this one.
   */
  openMap(lens?: LensDefinition | null): void {
    this.map.open(undefined, lens ?? null);
  }

  /**
   * Pass a change of lens on to a map opened from the lens bar. A no-op for the plain plan reached
   * from the Manifest menu, and for a closed map.
   */
  syncMapLens(lens: LensDefinition | null): void {
    this.map.syncLens(lens);
  }

  /**
   * Mount a control directly under the manifest bar, in the same corner.
   *
   * The counterpart of `QuickRender.mountLeading` on the other side of the canvas column, and for
   * the same reason: this corner belongs to *reading the site as a document* rather than to the
   * manifest specifically, and the tag index is the second such reading. They share the corner so
   * they read as one cluster, and so a second overlay does not have to guess how wide this bar
   * currently is — a width that changes as soon as anything is planted.
   *
   * The sheet drops to clear the taller corner, which is a thing only this method can know.
   */
  mountTrailing(element: HTMLElement): void {
    this.corner.appendChild(element);
    this.root.classList.add('has-trailing');
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(view?: ManifestView): void {
    if (view) this.view = view;
    this.sheet.hidden = false;
    this.openButton.setAttribute('aria-expanded', 'true');
    this.openButton.classList.add('is-open');
    this.plantLimit = PLANT_PAGE_SIZE;
    this.render();
    this.searchInput.focus();
  }

  close(): void {
    if (this.map.isOpen) {
      // The map opened from here, so Escape steps back to the sheet rather than closing both.
      this.map.close();
      return;
    }
    this.sheet.hidden = true;
    this.openButton.setAttribute('aria-expanded', 'false');
    this.openButton.classList.remove('is-open');
    this.openButton.focus();
  }

  /** Drop the cached manifest and redraw whatever is on screen. */
  invalidate(): void {
    this.cache = null;
    this.pointerCache = null;
    this.requestRender();
  }

  private requestRender(): void {
    if (this.frameHandle !== null) return;
    const schedule =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (callback: FrameRequestCallback): number =>
            setTimeout(() => callback(0), 16) as unknown as number;
    this.frameHandle = schedule(() => {
      this.frameHandle = null;
      this.refreshCount();
      if (this.isOpen) this.render();
      this.map.refresh();
      // Selection and unit changes reach here without touching the manifest, and both change what a
      // callout draws — so the overlay is re-solved on the same beat as everything else.
      this.pointers?.invalidate();
    });
  }

  /** The manifest for the current document, built at most once per edit. */
  private manifest(): PlantManifest {
    this.cache ??= buildPlantManifest({
      instances: this.deps.editor.world.all(),
      catalog: this.deps.editor.catalog,
      groups: this.deps.editor.groups.all(),
    });
    return this.cache;
  }

  private get unit(): 'meters' | 'feet' {
    return this.deps.preferences.unit;
  }

  /* ---------------------------------- Pointers ---------------------------------- */

  /**
   * The Pointers toggle, its detail menu, and the overlay they drive.
   *
   * The toggle sits in the manifest's own bar rather than in the toolbar because it is the same
   * subject read two ways — one opens the schedule, the other prints it on the garden — and because
   * a callout drawing is a thing you do *to the view*, which is what this bar is for.
   */
  private buildPointers(): void {
    const camera = this.deps.camera;
    const canvas = this.deps.canvas;
    if (!camera || !canvas) return;

    const preferences = this.deps.preferences;
    this.pointers = new ScenePointers({
      host: this.root,
      canvas,
      camera,
      targets: () => this.pointerTargets(),
      // No assertion: the two unions are declared separately, and letting the type-check see that
      // they agree is what catches it if one of them ever gains a member the other lacks.
      detail: () => preferences.pointerDetail,
      isSelected: (id) => this.deps.editor.selection.isSelected(id),
      isHot: (id) => this.hot.has(id),
      onSelect: (id, additive) => this.select([id], additive),
      onFocus: (id) => this.focus([id]),
      onHover: (id) => this.setHot(id === null ? [] : [id]),
    });

    this.pointerButton = el('button', {
      class: 'lg-manifest__pointers',
      title:
        'Pointers — label every plant and structure in the view with its manifest tag number.\n' +
        'Numbers are the manifest’s own, so they change when plants are added or removed.',
      attrs: { type: 'button', 'aria-pressed': 'false' },
      on: { click: () => this.togglePointers() },
    });
    this.pointerButton.append(
      el('span', { class: 'lg-manifest__pointersicon', html: icon('pin', 15) }),
      el('span', { text: 'Pointers' }),
    );

    this.pointerMenu = this.buildPointerMenu();
    this.bar.append(
      el('div', { class: 'lg-manifest__divider' }),
      this.pointerButton,
      el('button', {
        class: 'lg-manifest__pointersmore',
        html: icon('chevron', 12),
        title: 'What the pointers say',
        attrs: { type: 'button', 'aria-label': 'Pointer options', 'aria-expanded': 'false' },
        on: { click: () => this.togglePointerMenu() },
      }),
    );
    this.root.appendChild(this.pointerMenu);

    // Restored rather than defaulted off: someone who designs with the labels up wants them up when
    // they come back, the same way the grid and the unit are remembered.
    this.setPointers(preferences.pointers);
  }

  private buildPointerMenu(): HTMLElement {
    const preferences = this.deps.preferences;
    const details: readonly (readonly [PointerDetail, string])[] = [
      ['tag', 'Number'],
      ['name', 'Name'],
      ['full', 'Full'],
    ];
    const segment = el('div', {
      class: 'lg-segment lg-segment--tight',
      attrs: { role: 'group', 'aria-label': 'Pointer detail' },
    });
    for (const [value, label] of details) {
      segment.appendChild(
        el('button', {
          class: `lg-segment__item${preferences.pointerDetail === value ? ' is-active' : ''}`,
          text: label,
          attrs: { type: 'button', 'data-detail': value },
          on: {
            click: () => {
              preferences.setPointerDetail(value);
              for (const button of segment.querySelectorAll('button')) {
                button.classList.toggle('is-active', button.dataset.detail === value);
              }
              this.pointers?.invalidate();
            },
          },
        }),
      );
    }

    return el(
      'div',
      { class: 'lg-pointermenu', attrs: { hidden: true } },
      el(
        'div',
        { class: 'lg-pointermenu__group' },
        el('span', { class: 'lg-pointermenu__label', text: 'Detail' }),
        segment,
      ),
      el(
        'div',
        { class: 'lg-pointermenu__group' },
        el('span', { class: 'lg-pointermenu__label', text: 'Show' }),
        this.pointerCheck('Selection only', preferences.pointerSelectionOnly, (on) => {
          preferences.setPointerSelectionOnly(on);
        }),
        this.pointerCheck('Buildings and paving', preferences.pointerObjects, (on) => {
          preferences.setPointerObjects(on);
        }),
      ),
      el('p', {
        class: 'lg-pointermenu__note',
        text: 'Numbers match the Plants tab. They renumber when plants are added or removed.',
      }),
    );
  }

  private pointerCheck(label: string, checked: boolean, onChange: (on: boolean) => void): HTMLElement {
    const input = el('input', {
      attrs: { type: 'checkbox', ...(checked ? { checked: true } : {}) },
      on: {
        change: (event) => {
          onChange((event.target as HTMLInputElement).checked);
          this.pointerCache = null;
          this.pointers?.invalidate();
        },
      },
    });
    return el('label', { class: 'lg-pointermenu__check' }, input, el('span', { text: label }));
  }

  private togglePointerMenu(): void {
    const menu = this.pointerMenu;
    if (!menu) return;
    menu.hidden = !menu.hidden;
    this.root
      .querySelector('.lg-manifest__pointersmore')
      ?.setAttribute('aria-expanded', String(!menu.hidden));
  }

  /** Whether the callouts are drawn over the scene. */
  get pointersVisible(): boolean {
    return this.pointers?.isVisible ?? false;
  }

  togglePointers(): void {
    this.setPointers(!this.pointersVisible);
  }

  setPointers(visible: boolean): void {
    if (!this.pointers) return;
    this.pointers.setVisible(visible);
    this.deps.preferences.setPointers(visible);
    this.pointerButton?.classList.toggle('is-on', visible);
    this.pointerButton?.setAttribute('aria-pressed', String(visible));
    if (!visible && this.pointerMenu) this.pointerMenu.hidden = true;
  }

  /**
   * Advance the overlay. Driven from the app's render loop, because a callout has to follow its plant
   * on every frame the camera moves rather than on every frame the document changes.
   */
  tickPointers(): void {
    this.pointers?.tick();
  }

  /**
   * Everything worth pointing at in the current document.
   *
   * Built from the manifest — so the tag numbers *are* the manifest's numbers, not a second series
   * derived alongside them — and cached with it, since re-deriving four thousand targets per frame
   * would spend the whole budget on labels that did not move. Hidden plants and objects are skipped:
   * there is nothing on screen to point at, and a leader line to an invisible tree is a bug report
   * waiting to happen.
   */
  private pointerTargets(): readonly PointerTarget[] {
    const preferences = this.deps.preferences;
    if (this.pointerCache === null) {
      const manifest = this.manifest();
      const targets: PointerTarget[] = [];
      for (const plant of manifest.plants) {
        if (plant.hidden) continue;
        const instance = this.deps.editor.world.get(plant.id);
        if (!instance) continue;
        targets.push(
          plantPointerTarget(
            plant,
            manifest.colors.get(plant.definitionId) ?? '#3f8f45',
            instance.position.y,
            this.unit,
          ),
        );
      }
      if (preferences.pointerObjects) {
        const objects = this.deps.siteObjects?.() ?? [];
        const tags = assignObjectTags(objects);
        for (const object of objects) {
          if (object.hidden) continue;
          targets.push(objectPointerTarget(object, tags.get(object.id) ?? '?', this.unit));
        }
      }
      this.pointerCache = targets;
    }
    if (!preferences.pointerSelectionOnly) return this.pointerCache;
    const { selection } = this.deps.editor;
    return this.pointerCache.filter((target) => selection.isSelected(target.id));
  }

  /**
   * Light a set of ids in both views at once.
   *
   * Applied by toggling a class on the rows already on screen rather than by re-rendering the table:
   * a hover that rebuilt a four-hundred-row sheet would drop the pointer out of the row it is over,
   * and the flicker would be the only thing anyone noticed about the feature.
   */
  private setHot(ids: readonly string[]): void {
    const next = new Set(ids);
    if (next.size === this.hot.size && [...next].every((id) => this.hot.has(id))) return;
    for (const [id, rows] of this.rowsById) {
      const on = next.has(id);
      for (const row of rows) row.classList.toggle('is-hot', on);
    }
    this.hot = next;
    this.pointers?.invalidate();
  }

  /** Register a row against the ids it stands for, so {@link setHot} can find it later. */
  private trackRow(row: HTMLElement, ids: readonly string[]): HTMLElement {
    for (const id of ids) {
      const existing = this.rowsById.get(id);
      if (existing) existing.push(row);
      else this.rowsById.set(id, [row]);
    }
    row.addEventListener('pointerenter', () => this.setHot(ids));
    row.addEventListener('pointerleave', () => this.setHot([]));
    return row;
  }

  /* --------------------------------- Construction --------------------------------- */

  private buildSheet(): HTMLElement {
    const views: readonly (readonly [ManifestView, string, string])[] = [
      ['schedule', 'Schedule', 'One line per species — the sheet you order from'],
      ['plants', 'Plants', 'Every specimen, tagged and numbered'],
      ...(this.deps.resolver
        ? ([['ecology', 'Ecology', 'What this planting feeds and shelters, month by month']] as const)
        : []),
    ];
    for (const [view, label, hint] of views) {
      this.tabs.appendChild(
        el('button', {
          class: 'lg-segment__item',
          text: label,
          title: hint,
          attrs: { type: 'button', 'data-view': view },
          on: { click: () => this.setView(view) },
        }),
      );
    }

    return el(
      'div',
      {
        class: 'lg-manifest__sheet',
        attrs: { role: 'dialog', 'aria-label': 'Plant manifest', hidden: true },
      },
      el(
        'header',
        { class: 'lg-manifest__head' },
        el('span', { class: 'lg-manifest__headicon', html: icon('list', 16) }),
        el(
          'div',
          { class: 'lg-manifest__heading' },
          el('h2', { class: 'lg-manifest__title', text: 'Plant manifest' }),
          this.subtitle,
        ),
        this.tabs,
        el('button', {
          class: 'lg-manifest__iconbutton',
          html: icon('close', 15),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the manifest' },
          on: { click: () => this.close() },
        }),
      ),
      el(
        'div',
        { class: 'lg-manifest__tools' },
        el('div', { class: 'lg-manifest__searchwrap' },
          el('span', { class: 'lg-manifest__searchicon', html: icon('search', 14) }),
          this.searchInput,
        ),
        el('label', { class: 'lg-manifest__sortwrap' },
          el('span', { class: 'lg-manifest__sortlabel', text: 'Sort' }),
          this.sortSelect,
        ),
        el('div', { class: 'lg-manifest__spacer' }),
        this.actionButton('top', 'Plan map', 'Plot the manifest on a top-down plan', () => this.map.open()),
        this.actionButton('duplicate', 'Copy', 'Copy this table to the clipboard as CSV', () => {
          void this.copyCsv();
        }),
        this.actionButton('download', 'CSV', 'Download this table as a CSV file', () => this.downloadCsv()),
      ),
      this.body,
      this.footer,
    );
  }

  private actionButton(
    iconName: IconName,
    label: string,
    title: string,
    onClick: () => void,
  ): HTMLElement {
    return el(
      'button',
      {
        class: 'lg-manifest__action',
        title,
        attrs: { type: 'button' },
        on: { click: onClick },
      },
      el('span', { class: 'lg-manifest__actionicon', html: icon(iconName, 14) }),
      el('span', { text: label }),
    );
  }

  private setView(view: ManifestView): void {
    this.view = view;
    for (const button of this.tabs.querySelectorAll('button')) {
      button.classList.toggle('is-active', button.dataset.view === view);
    }
    this.plantLimit = PLANT_PAGE_SIZE;
    if (this.isOpen) this.render();
  }

  /* ----------------------------------- Rendering ----------------------------------- */

  /** The bar's live count, which is on screen whether or not the sheet is. */
  private refreshCount(): void {
    const count = this.deps.editor.world.count;
    this.countChip.textContent = String(count);
    this.countChip.hidden = count === 0;
  }

  private render(): void {
    const manifest = this.manifest();
    this.sortSelect.value = this.sort;
    this.sortSelect.parentElement?.toggleAttribute('hidden', this.view !== 'schedule');

    this.subtitle.textContent = summaryLine(manifest, this.unit);
    // The row index is rebuilt with the table it points into — holding elements that have just been
    // thrown away would leak the whole sheet, one render at a time.
    this.rowsById.clear();
    clear(this.body);
    this.body.appendChild(
      this.view === 'ecology'
        ? this.ecologyView(manifest)
        : this.view === 'schedule'
          ? this.scheduleTable(manifest)
          : this.plantsTable(manifest),
    );
    this.note.textContent = noteLine(manifest, this.view);
  }

  /* ------------------------------------ Ecology ------------------------------------ */

  /**
   * The planting as a food and habitat calendar.
   *
   * Every band here was derived from the same resolved phenology the canvas is drawing: the month a
   * berry appears on this chart is the month the renderer starts drawing fruit on that shrub. That is
   * the point of routing both through one resolver, and it is why this view can be trusted rather
   * than merely consulted.
   *
   * Gaps come first, above the bands. The bands are the evidence; the gaps are the finding, and a
   * report that buries its finding under its evidence is a report nobody reads to the end of.
   */
  private ecologyView(manifest: PlantManifest): HTMLElement {
    const resolver = this.deps.resolver?.();
    if (!resolver) return el('p', { class: 'lg-ecology__empty', text: 'No site to report against.' });

    const localization = this.deps.localization?.() ?? null;
    const entries: CalendarEntry[] = [];
    for (const species of manifest.species) {
      const definition = this.deps.editor.catalog.find(species.definitionId);
      if (!definition) continue;
      entries.push({
        definition,
        ecology: resolver.resolve(definition).ecology,
        count: species.count,
      });
    }

    const root = el('div', { class: 'lg-ecology' });
    if (entries.length === 0) {
      root.appendChild(
        el('p', {
          class: 'lg-ecology__empty',
          text: 'Nothing is planted yet. Plant something and this becomes a wildlife calendar.',
        }),
      );
      return root;
    }

    const calendar = buildWildlifeCalendar(entries, resolver.localization);

    root.appendChild(
      el('p', {
        class: 'lg-ecology__intro',
        text:
          `${calendar.describedSpecies} of ${calendar.totalSpecies} species on this site carry ` +
          'ecological data. Months are derived from each plant’s own bloom and fruiting here' +
          (localization ? `, in ${localization.regionLabel}.` : ', against the reference climate.'),
      }),
    );

    for (const gap of calendar.gaps) {
      root.appendChild(
        el(
          'div',
          { class: 'lg-ecology__gap' },
          el('span', { class: 'lg-ecology__gaptitle', text: `${gap.label} gap` }),
          el('p', { class: 'lg-ecology__gapbody', text: gap.message }),
        ),
      );
    }

    if (calendar.food.length > 0) {
      root.appendChild(this.calendarBlock('Food', calendar.food));
    }
    if (calendar.habitat.length > 0) {
      root.appendChild(this.calendarBlock('Habitat', calendar.habitat));
    }
    if (calendar.food.length === 0 && calendar.habitat.length === 0) {
      root.appendChild(
        el('p', {
          class: 'lg-ecology__empty',
          text:
            'None of the species planted here carry ecological data yet. The catalog is enriched a ' +
            'species at a time, and a plant with no record contributes nothing rather than a guess.',
        }),
      );
    }
    return root;
  }

  private calendarBlock(title: string, rows: readonly CalendarRow<string>[]): HTMLElement {
    const grid = el('div', { class: 'lg-calendar' });
    grid.appendChild(el('span'));
    for (const month of MONTH_LABELS) {
      grid.appendChild(el('span', { class: 'lg-calendar__head', text: month.slice(0, 1) }));
    }
    for (const row of rows) {
      grid.appendChild(
        el('span', {
          class: 'lg-calendar__label',
          text: row.label,
          title: `${row.label} — from ${row.contributors} species on this site`,
        }),
      );
      row.months.forEach((value, index) => {
        grid.appendChild(
          el('span', {
            class: 'lg-calendar__cell',
            title: `${row.label}, ${MONTH_LABELS[index]}`,
            // Opacity rather than a colour ramp: the bands are one quantity at twelve strengths, and
            // a hue scale would imply categories the data does not have.
            style: value < 0.02 ? {} : { background: `rgb(108 194 106 / ${Math.round(value * 88)}%)` },
          }),
        );
      });
    }
    return el(
      'section',
      { class: 'lg-ecology__block' },
      el('h3', { class: 'lg-ecology__title', text: title }),
      grid,
    );
  }

  private scheduleTable(manifest: PlantManifest): HTMLElement {
    const species = sortSpecies(this.filteredSpecies(manifest), this.sort);
    if (species.length === 0) return this.emptyState(manifest);

    const rows: Child[] = [];
    if (this.sort === 'section') {
      for (const section of manifest.sections) {
        const members = section.species.filter((entry) => species.includes(entry));
        if (members.length === 0) continue;
        rows.push(
          el(
            'tr',
            { class: 'lg-manifest__sectionrow' },
            el('th', {
              class: 'lg-manifest__sectionhead',
              attrs: { colspan: '7', scope: 'colgroup' },
            },
              el('span', { text: section.label }),
              el('span', {
                class: 'lg-manifest__sectioncount',
                text: `${section.count} plant${section.count === 1 ? '' : 's'}`,
              }),
            ),
          ),
        );
        for (const entry of members) rows.push(this.speciesRow(entry));
      }
    } else {
      for (const entry of species) rows.push(this.speciesRow(entry));
    }

    return table(
      ['Qty', 'Plant', 'Size today', 'Specified size', 'At maturity', 'Spacing', 'Canopy'],
      rows,
      'lg-manifest__table lg-manifest__table--schedule',
    );
  }

  private speciesRow(entry: ManifestSpecies): HTMLElement {
    const unit = this.unit;
    const selected = entry.plantIds.every((id) => this.deps.editor.selection.isSelected(id));

    const row = el(
      'tr',
      {
        class: `lg-manifest__row${selected ? ' is-selected' : ''}`,
        attrs: { tabindex: '0', role: 'button' },
        title: `Select all ${entry.count} · double-click to fly there`,
        on: {
          click: (event) => this.select(entry.plantIds, (event as MouseEvent).shiftKey),
          dblclick: () => this.focus(entry.plantIds),
          keydown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.select(entry.plantIds, false);
          },
        },
      },
      el('td', {},
        el('span', { class: 'lg-manifest__qty' },
          el('span', { class: 'lg-manifest__swatch', style: { background: entry.color } }),
          el('span', { class: 'lg-manifest__qtynum', text: String(entry.count) }),
        ),
      ),
      el('td', { class: 'lg-manifest__namecol' },
        el('span', { class: 'lg-manifest__plantcell' },
          el('span', { class: 'lg-manifest__botanical', text: entry.fullName }),
          el('span', { class: 'lg-manifest__common', text: entry.commonName }),
          entry.ailingCount > 0
            ? el('span', {
                class: 'lg-manifest__flag lg-manifest__flag--warn',
                text: `${entry.ailingCount} ailing`,
                title: 'These specimens are set below 60% health',
              })
            : null,
          entry.hiddenCount > 0
            ? el('span', {
                class: 'lg-manifest__flag',
                text: `${entry.hiddenCount} hidden`,
                title: 'Hidden in the viewport — still counted here',
              })
            : null,
        ),
      ),
      el('td', {
        text:
          entry.count === 1 || entry.maxHeightMeters - entry.minHeightMeters < 0.05
            ? formatLength(entry.maxHeightMeters, unit)
            : `${formatLength(entry.minHeightMeters, unit)}–${formatLength(entry.maxHeightMeters, unit)}`,
        title: `${Math.round(entry.averageMaturity * 100)}% grown on average`,
      }),
      el('td', { class: 'lg-manifest__spec', text: containerLabel(entry.commonContainerStage) }),
      el('td', {
        text: `${formatLength(entry.matureHeightMeters, unit)} × ${formatLength(entry.matureSpreadMeters, unit)}`,
      }),
      el('td', { text: formatLength(entry.recommendedSpacingMeters, unit) }),
      el('td', {},
        el('span', { class: 'lg-manifest__canopy' },
          el('span', { text: formatArea(entry.canopyAreaSquareMeters, unit) }),
          el('span', {
            class: 'lg-manifest__mature',
            text: `→ ${formatArea(entry.matureCanopyAreaSquareMeters, unit)}`,
            title: 'Ground this species will cover once grown',
          }),
          el('button', {
            class: 'lg-manifest__rowmap',
            html: icon('top', 13),
            title: `Show the ${entry.count} ${entry.commonName} on the plan`,
            attrs: { type: 'button', 'aria-label': `Show ${entry.fullName} on the plan` },
            on: {
              click: (event) => {
                event.stopPropagation();
                this.map.open(entry.definitionId);
              },
            },
          }),
        ),
      ),
    );
    return this.trackRow(row, entry.plantIds);
  }

  private plantsTable(manifest: PlantManifest): HTMLElement {
    const plants = manifest.plants.filter((plant) => matchesQuery(plant, this.query));
    if (plants.length === 0) return this.emptyState(manifest);

    const shown = plants.slice(0, this.plantLimit);
    const rows: Child[] = shown.map((plant) => this.plantRow(plant, manifest));

    const element = table(
      ['Tag', 'Plant', 'Size', 'Grown', 'Age', 'Specified size', 'Position', 'Group'],
      rows,
      'lg-manifest__table lg-manifest__table--plants',
    );

    if (plants.length > shown.length) {
      return el(
        'div',
        { class: 'lg-manifest__paged' },
        element,
        el(
          'button',
          {
            class: 'lg-manifest__more',
            attrs: { type: 'button' },
            on: {
              click: () => {
                this.plantLimit += PLANT_PAGE_SIZE;
                this.render();
              },
            },
          },
          el('span', {
            text: `Show ${Math.min(PLANT_PAGE_SIZE, plants.length - shown.length)} more — ${shown.length} of ${plants.length} listed`,
          }),
        ),
      );
    }
    return element;
  }

  private plantRow(plant: ManifestPlant, manifest: PlantManifest): HTMLElement {
    const unit = this.unit;
    const selected = this.deps.editor.selection.isSelected(plant.id);
    const row = el(
      'tr',
      {
        class: `lg-manifest__row${selected ? ' is-selected' : ''}`,
        attrs: { tabindex: '0', role: 'button' },
        title: 'Click to select · shift-click to add · double-click to fly there',
        on: {
          click: (event) => this.select([plant.id], (event as MouseEvent).shiftKey),
          dblclick: () => this.focus([plant.id]),
          keydown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.select([plant.id], false);
          },
        },
      },
      el('td', {},
        el('span', { class: 'lg-manifest__tag' },
          el('span', {
            class: 'lg-manifest__swatch',
            style: { background: manifest.colors.get(plant.definitionId) ?? '#3f8f45' },
          }),
          el('span', { text: String(plant.index) }),
        ),
      ),
      el('td', { class: 'lg-manifest__namecol' },
        el('span', { class: 'lg-manifest__plantcell' },
          el('span', { class: 'lg-manifest__botanical', text: plant.fullName }),
          el('span', { class: 'lg-manifest__common', text: plant.commonName }),
          plant.health <= AILING_HEALTH
            ? el('span', {
                class: 'lg-manifest__flag lg-manifest__flag--warn',
                text: `${Math.round(plant.health * 100)}% health`,
              })
            : null,
          plant.hidden ? el('span', { class: 'lg-manifest__flag', text: 'hidden' }) : null,
          plant.locked ? el('span', { class: 'lg-manifest__flag', text: 'locked' }) : null,
        ),
      ),
      el('td', {
        text: `${formatLength(plant.heightMeters, unit)} × ${formatLength(plant.spreadMeters, unit)}`,
      }),
      el('td', { text: `${Math.round(plant.maturity * 100)}%` }),
      el('td', { text: formatAge(plant.ageYears) }),
      el('td', { class: 'lg-manifest__spec', text: containerLabel(plant.containerStage) }),
      el('td', {
        class: 'lg-manifest__position',
        text: `${formatLength(plant.x, unit)}, ${formatLength(plant.z, unit)}`,
        title: 'Distance east and south of the site centre',
      }),
      el('td', { class: 'lg-manifest__group', text: plant.groupName ?? '—' }),
    );
    return this.trackRow(row, [plant.id]);
  }

  private emptyState(manifest: PlantManifest): HTMLElement {
    const planted = manifest.totalPlants > 0;
    return el(
      'div',
      { class: 'lg-manifest__empty' },
      el('span', { class: 'lg-manifest__emptyicon', html: icon('plant', 22) }),
      el('p', {
        text: planted
          ? `Nothing matches “${this.query}”.`
          : 'Nothing planted yet. Place something from the Flora palette and it will be scheduled here.',
      }),
    );
  }

  private filteredSpecies(manifest: PlantManifest): ManifestSpecies[] {
    const needle = this.query.trim().toLowerCase();
    if (needle === '') return [...manifest.species];
    return manifest.species.filter(
      (entry) =>
        entry.fullName.toLowerCase().includes(needle) ||
        entry.commonName.toLowerCase().includes(needle) ||
        entry.definitionId.toLowerCase().includes(needle),
    );
  }

  /* ----------------------------------- Wiring out ----------------------------------- */

  private select(ids: readonly string[], additive: boolean): void {
    const { selection } = this.deps.editor;
    if (additive) {
      for (const id of ids) selection.select(id, true);
    } else {
      selection.selectOnly(ids);
    }
    this.requestRender();
  }

  private focus(ids: readonly string[]): void {
    this.select(ids, false);
    this.deps.onFocus?.(ids);
  }

  /** CSV for whichever table is on screen, honouring the filter — you export what you are reading. */
  private csv(): string {
    const manifest = this.manifest();
    if (this.view === 'ecology') return this.ecologyCsv(manifest);
    return this.view === 'schedule'
      ? scheduleCsv(sortSpecies(this.filteredSpecies(manifest), this.sort), this.unit)
      : plantsCsv(
          manifest.plants.filter((plant) => matchesQuery(plant, this.query)),
          this.unit,
        );
  }

  /**
   * The calendar as twelve numbered columns, plus the gaps as rows of their own.
   *
   * The gaps go in the same file rather than a second one because they are the part worth acting on,
   * and a spreadsheet of bands with the findings left behind in the app is how a report gets read
   * once and never used.
   */
  private ecologyCsv(manifest: PlantManifest): string {
    const resolver = this.deps.resolver?.();
    if (!resolver) return '';
    const entries: CalendarEntry[] = [];
    for (const species of manifest.species) {
      const definition = this.deps.editor.catalog.find(species.definitionId);
      if (!definition) continue;
      entries.push({
        definition,
        ecology: resolver.resolve(definition).ecology,
        count: species.count,
      });
    }
    const calendar = buildWildlifeCalendar(entries, resolver.localization);

    const lines = [`Kind,Resource,Species,${MONTH_LABELS.join(',')}`];
    for (const [kind, rows] of [
      ['Food', calendar.food],
      ['Habitat', calendar.habitat],
    ] as const) {
      for (const row of rows) {
        lines.push(
          `${kind},"${row.label}",${row.contributors},` +
            row.months.map((value) => value.toFixed(2)).join(','),
        );
      }
    }
    for (const gap of calendar.gaps) {
      lines.push(`Gap,"${gap.label}",,"${gap.message.replace(/"/g, '""')}"`);
    }
    return `${lines.join('\n')}\n`;
  }

  private async copyCsv(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(this.csv());
      this.flashNote('Copied to the clipboard.');
    } catch {
      this.flashNote('Could not reach the clipboard — use the CSV download instead.');
    }
  }

  private downloadCsv(): void {
    const blob = new Blob([this.csv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = el('a', {
      attrs: { href: url, download: `plant-${this.view}.csv` },
      style: { display: 'none' },
    });
    this.root.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    this.flashNote(`Exported plant-${this.view}.csv`);
  }

  /** Say something in the footer, then let the standing summary take the line back. */
  private flashNote(message: string): void {
    this.note.textContent = message;
    setTimeout(() => {
      if (this.isOpen) this.note.textContent = noteLine(this.manifest(), this.view);
    }, 2600);
  }

  dispose(): void {
    if (this.frameHandle !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.frameHandle);
    }
    this.frameHandle = null;
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.map.dispose();
    this.pointers?.dispose();
    this.pointers = null;
    this.rowsById.clear();
    this.root.remove();
  }
}

/* -------------------------------------- Helpers -------------------------------------- */

function table(headings: readonly string[], rows: readonly Child[], className: string): HTMLElement {
  const head = el('tr');
  for (const heading of headings) {
    head.appendChild(el('th', { text: heading, attrs: { scope: 'col' } }));
  }
  const bodyRows = el('tbody');
  for (const row of rows) if (row) bodyRows.appendChild(row as Node);
  return el('table', { class: className }, el('thead', {}, head), bodyRows);
}

function summaryLine(manifest: PlantManifest, unit: 'meters' | 'feet'): string {
  if (manifest.totalPlants === 0) return 'Nothing planted yet';
  return [
    `${manifest.totalPlants} plant${manifest.totalPlants === 1 ? '' : 's'}`,
    `${manifest.totalSpecies} species`,
    `${formatArea(manifest.canopyAreaSquareMeters, unit)} canopy today`,
    `${formatArea(manifest.matureCanopyAreaSquareMeters, unit)} grown`,
  ].join('  ·  ');
}

function noteLine(manifest: PlantManifest, view: ManifestView): string {
  const parts: string[] = [
    view === 'ecology'
      ? 'Bands are derived from each plant’s bloom and fruiting on this site'
      : view === 'schedule'
        ? 'Click a line to select every plant of that species'
        : 'Click a row to select · double-click to fly there',
  ];
  if (manifest.ailingCount > 0) parts.push(`${manifest.ailingCount} below 60% health`);
  if (manifest.hiddenCount > 0) parts.push(`${manifest.hiddenCount} hidden in the viewport`);
  if (manifest.lockedCount > 0) parts.push(`${manifest.lockedCount} locked`);
  return parts.join('  ·  ');
}
